// Rocks, rock pillars, broken ruin walls and crates, all drawn inside their exact collision
// footprint (cylinder radius / box extents, y0..y1).
//
// Round 2 (golden_grass_mechs.png): boulders and pillars are rounded, chunky, cool teal-grey
// stone with moss creeping over every upward face, smooth-shaded and textured with a painted
// stroke detail in the material; they're merged into one smooth mesh, and so are the ruins, now
// dry-stone walls of rounded field stones (no cut blocks anywhere). Crates stay crisp boxes.

import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { World, Box, Cyl, NO_GROUND } from '../../../sim/world';
import { hash32, valueNoise } from '../../../sim/core';
import { isBoulderPile } from '../../../sim/destruct';
import { PAL } from '../palette';
import { Geo, C, Jit } from './geo';
import { Clock, type Pause } from '../warm';

// stone: cool slate under warm light, so the sun side reads grey-gold and the shade teal
const cStone = new THREE.Color(0x66706c), cStoneWarm = new THREE.Color(0x857d6c), cStoneDark = new THREE.Color(0x2a3a3c);
const cLichen = new THREE.Color(0xb8b48a), cLichenRust = new THREE.Color(0xc0844a);
const SUN = new THREE.Vector3(-0.55, 0.42, -0.72).normalize(); // matches HAZE.uSunDir
const cMoss = new THREE.Color(0x5f7a34), cMossDeep = new THREE.Color(0x2f4d3c), cMossSun = new THREE.Color(0x9c9a3a);
const cBrick = C(PAL.brick), cTan = C(PAL.tan), cDirt = C(PAL.dirt);
const cWood = C(PAL.wood), cWoodDark = C(PAL.wood, 0.7), cCard = C(PAL.tan, 1.05), cRed = C(PAL.toasterRed), cInk = C(PAL.inkDark);
const tmp = new THREE.Color();

/** Faceted blocks: crates. */
export function buildProps(world: World): Geo {
  const g = new Geo();
  for (const b of world.boxes) {
    if (b.kind === 'crate') crate(b, g);
  }
  return g;
}

/** Smooth mossy stone: every rock and pillar merged into one indexed geometry, in slices (a
 *  background island build: client/render/warm.ts). `framers` receives the three big meadow
 *  boulders (sim variants 46-48) as their own geometry: dark teal-grey framing masses drawn with a
 *  bare (moss-free) material. `monoliths` receives each standing stone's index run in the merged
 *  geometry (by pillar variant), so a toppled one can be hidden from it (world/stones.ts). */
export function* buildRocksSteps(world: World, quality: number, pause: Pause, framers?: THREE.BufferGeometry[], monoliths?: Map<number, { start: number; count: number }>): Generator<unknown, THREE.BufferGeometry, unknown> {
  const clk = new Clock(pause);
  const parts: THREE.BufferGeometry[] = [];
  const mono = new Map<number, number>();
  for (const c of world.cyls) {
    yield* clk.tick('rocks');
    // the big sim rocks are the meadow's dark framing masses: darker than the pale standing stones
    if (c.kind === 'rock') {
      const framer = c.variant >= 46 && c.variant <= 48 && c.r > 3;
      _mossMode = 1;
      const g = boulder(c.x, c.z, c.r, c.y0, c.y1, hash32(c.variant, 0x70c, Math.round(c.x * 7)), quality, !framer);
      _mossMode = 0;
      // chipped debris round its foot (half-buried, under step height)
      debris(c.x, c.z, c.r, world, hash32(c.variant, 0xdeb), quality <= 1 ? 2 : 5, parts);
      // the three big meadow boulders (sim variants 46-48) are dark teal-grey framing masses
      if (framer) (framers ?? parts).push(tintRGB(g, 0.3, 0.4, 0.43));
      // warm weathered sandstone like the spires (a cold green-grey read as a mossy box)
      else parts.push(tintRGB(g, c.r > 1.4 ? 0.95 : 1.05, c.r > 1.4 ? 0.72 : 0.8, c.r > 1.4 ? 0.5 : 0.58));
    }
    else if (c.kind === 'pillar') {
      // a third of the standing stones become mossy boulder piles (same cylinder, same crown height)
      if (isBoulderPile(c)) boulderPile(c, quality, parts);
      else { mono.set(parts.length, c.variant); pillar(c, quality, parts); }
    }
  }
  // ruins are dry-stone field walls: rounded, uneven stones, never cut blocks (James: no Minecraft stone)
  for (const b of world.boxes) if (b.kind === 'ruin') { yield* clk.tick('rocks:ruins'); fieldWall(b, quality, parts); }
  yield* clk.tick('rocks:pebbles');
  pebbles(world, quality, parts);
  if (monoliths) {
    let io = 0;
    parts.forEach((p, k) => {
      const v = mono.get(k);
      if (v !== undefined) monoliths.set(v, { start: io, count: p.index!.count });
      io += p.index!.count;
    });
  }
  yield* clk.tick('rocks:merge');
  return mergeAll(parts);
}

/** Multiply a geometry's vertex colours per channel. */
function tintRGB(g: THREE.BufferGeometry, r: number, gg: number, b: number) {
  const c = g.attributes.color as THREE.BufferAttribute;
  if (c) for (let i = 0; i < c.count; i++) c.setXYZ(i, c.getX(i) * r, c.getY(i) * gg, c.getZ(i) * b);
  return g;
}

/** Scale a geometry's vertex colours (dark set dressing: pebbles, rim outcrops). */
function darken(g: THREE.BufferGeometry, k: number) {
  const c = g.attributes.color as THREE.BufferAttribute;
  if (c) for (let i = 0; i < c.array.length; i++) (c.array as Float32Array)[i] *= k;
  return g;
}

export function mergeAll(parts: THREE.BufferGeometry[]) {
  let nv = 0, ni = 0;
  for (const p of parts) { nv += p.attributes.position.count; ni += p.index!.count; }
  const pos = new Float32Array(nv * 3), nrm = new Float32Array(nv * 3), col = new Float32Array(nv * 3);
  const idx = new Uint32Array(ni);
  let vo = 0, io = 0;
  for (const p of parts) {
    pos.set(p.attributes.position.array as Float32Array, vo * 3);
    nrm.set(p.attributes.normal.array as Float32Array, vo * 3);
    col.set(p.attributes.color.array as Float32Array, vo * 3);
    const src = p.index!.array;
    for (let i = 0; i < src.length; i++) idx[io + i] = src[i] + vo;
    vo += p.attributes.position.count; io += src.length;
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeBoundingSphere();
  g.computeBoundingBox();
  return g;
}

const _p = new THREE.Vector3(), _n = new THREE.Vector3();
/** 0: moss over the up faces (default), 1: a noise patch on the crown, 2: in the crevices only */
let _mossMode = 0;
/** > 0: press the top flat at this depth (dry-stone slabs) */
let _slabCut = 0;

/**
 * A rounded, chunky boulder fitted inside radius r and y0..y1: a noise-displaced sphere with a
 * few planar cuts (the Ghibli "soft block" read), flattened underneath and sunk into the ground.
 * Moss over upward faces, warm stone on the flanks, teal in the crevices.
 */
function boulder(x: number, z: number, r: number, y0: number, y1: number, seed: number, quality: number, moss: boolean, pebbleQ = -1) {
  const big = r > 1.4;
  // pebbles (quality < 0): rounded, never cut into shards (a cut 20-face icosahedron read as a pyramid)
  const pebble = quality < 0;
  const det = pebble ? (pebbleQ >= 2 ? 1 : 0) : quality <= 2 ? 1 : big ? 2 : 1;
  let g: THREE.BufferGeometry = new THREE.IcosahedronGeometry(1, det);
  g.deleteAttribute('uv');
  g.deleteAttribute('normal');
  g = mergeVertices(g);
  const j = new Jit(seed);
  // a few cut planes, pressed into the sphere: flat-ish facets with soft edges
  // the crown is always pressed flat so whoever stands on top stands on the stone
  const cuts: number[][] = [[0, 1, 0, pebble ? 0.8 : _slabCut > 0 ? _slabCut : j.range(0.74, 0.88)]];
  if (pebble) cuts[0][3] = 0.8;
  // not a bevelled box: three fractured variants -- a split block (one deep sheared face), a
  // cleft stone (two steep faces meeting in a ridge), and a rounded stone with chipped edges --
  // each with small chips knocked off its edges
  const variant = seed % 3;
  if (!pebble) {
    const a0 = j.range(0, Math.PI * 2);
    if (variant === 0) cuts.push([Math.cos(a0) * 0.96, j.range(-0.1, 0.25), Math.sin(a0) * 0.96, j.range(0.32, 0.5)]);
    else if (variant === 1) for (const da of [-0.75, 0.75]) cuts.push([Math.cos(a0 + da) * 0.9, 0.3, Math.sin(a0 + da) * 0.9, j.range(0.5, 0.62)]);
    for (let k = 0; k < (variant === 2 ? 6 : 3); k++) {
      const a = j.range(0, Math.PI * 2), e = j.range(-0.1, 0.8);
      cuts.push([Math.cos(a) * Math.cos(e), Math.sin(e), Math.sin(a) * Math.cos(e), j.range(0.8, 0.9)]);
    }
  }
  const lean = j.range(-0.15, 0.15), so = seed & 0xffff;
  const shA = j.range(0, Math.PI * 2), shK = big ? j.range(0.12, 0.38) : 0;
  const shx = Math.cos(shA) * shK, shz = Math.sin(shA) * shK;
  const aspect = j.range(1.0, 1.55), aa = j.range(0, Math.PI), ca = Math.cos(aa), sa = Math.sin(aa);
  const pos = g.attributes.position as THREE.BufferAttribute;
  const h = y1 - y0;
  for (let i = 0; i < pos.count; i++) {
    _p.fromBufferAttribute(pos, i).normalize();
    let rr = 1 + (valueNoise(so, _p.x * 1.2 + 3, _p.z * 1.2 + _p.y * 1.1) - 0.5) * 0.45 + (valueNoise(so + 9, _p.x * 3.3, _p.z * 3.3 + _p.y * 2.9) - 0.5) * 0.14;
    for (const c of cuts) {
      const d = _p.x * c[0] + _p.y * c[1] + _p.z * c[2];
      if (d * rr > c[3]) rr = THREE.MathUtils.lerp(rr, c[3] / Math.max(d, 1e-3), c === cuts[0] ? 0.8 : c[3] < 0.7 ? 0.92 : 0.7);
    }
    _p.multiplyScalar(rr);
    // flatten the base
    if (_p.y < -0.55) _p.y = -0.55 + (_p.y + 0.55) * 0.25;
    { const ax = _p.x * ca + _p.z * sa, az = -_p.x * sa + _p.z * ca; _p.x = ax * aspect * ca - az * sa; _p.z = ax * aspect * sa + az * ca; }
    // big rocks lean: shear the upper body to one side (the fit below keeps it in the cylinder)
    _p.x += shx * (_p.y + 0.55); _p.z += shz * (_p.y + 0.55);
    pos.setXYZ(i, _p.x, _p.y, _p.z);
  }
  g.computeBoundingBox();
  const bb = g.boundingBox!;
  // fit: xz radius <= r (0.98 of it), y spans y0..y1 exactly
  let maxR = 0;
  for (let i = 0; i < pos.count; i++) maxR = Math.max(maxR, Math.hypot(pos.getX(i), pos.getZ(i)));
  const sxz = (r * 0.98) / maxR, sy = h / (bb.max.y - bb.min.y);
  for (let i = 0; i < pos.count; i++) {
    const py = (pos.getY(i) - bb.min.y) * sy;
    const t = py / h;
    pos.setXYZ(i, x + pos.getX(i) * sxz * (1 - lean * t * 0), y0 + py, z + pos.getZ(i) * sxz);
  }
  return chunky(g, seed, moss, y0, y1);
}

/**
 * Small stones scattered through the meadow (golden_grass_mechs.png's grey pebbles): 10-40 cm,
 * well under the 55 cm step height, so they're set dressing like the grass, not collision.
 * Merged into the rock mesh: no extra draw call.
 */
function pebbles(world: World, quality: number, out: THREE.BufferGeometry[]) {
  const n = [80, 120, 40, 170][Math.max(0, Math.min(3, quality))];
  const j = new Jit(world.seed ^ 0x9eb);
  const isl = world.islands[0];
  for (let i = 0, made = 0; i < n * 4 && made < n; i++) {
    const a = j.range(0, Math.PI * 2), r = Math.sqrt(j.next()) * isl.radius * 0.85;
    const x = isl.cx + Math.cos(a) * r, z = isl.cz + Math.sin(a) * r;
    if (world.townMask(x, z) > 0.05) continue;
    const gy = world.terrain(x, z);
    if (gy === NO_GROUND) continue;
    let clear = true;
    for (const p of world.path) if (Math.abs(p.x - x) < 2.4 && Math.abs(p.z - z) < 2.4) { clear = false; break; }
    if (!clear) continue;
    // clusters: 1-3 stones together
    const k = 1 + (j.next() * 3 | 0);
    for (let c = 0; c < k; c++) {
      const sr = j.range(0.1, 0.38) * (c ? 0.6 : 1);
      const px = x + j.range(-0.5, 0.5) * c, pz = z + j.range(-0.5, 0.5) * c;
      const py = world.terrain(px, pz);
      if (py === NO_GROUND) continue;
      out.push(darken(boulder(px, pz, sr, py - sr * 0.35, py + sr * j.range(0.35, 0.7), hash32(i, c, 0x9eb), -1, false, quality), j.range(0.4, 0.62)));
    }
    made++;
  }
}

/** Painted-chunky shading: facet normals softened 40% toward the smooth normal, so the stone
 *  shows its planes like the ref's brushwork rocks without hard CG edges. */
function chunky(g: THREE.BufferGeometry, seed: number, moss: boolean, y0: number, y1: number) {
  g.computeVertexNormals();
  const smooth = g.attributes.normal as THREE.BufferAttribute;
  const idx = g.index!;
  const P = g.attributes.position as THREE.BufferAttribute;
  const n = idx.count;
  const pos = new Float32Array(n * 3), nrm = new Float32Array(n * 3);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), f = new THREE.Vector3(), s = new THREE.Vector3();
  for (let t = 0; t < n; t += 3) {
    const ia = idx.getX(t), ib = idx.getX(t + 1), ic = idx.getX(t + 2);
    a.fromBufferAttribute(P, ia); b.fromBufferAttribute(P, ib); c.fromBufferAttribute(P, ic);
    f.subVectors(c, b).cross(a.sub(b)).normalize();
    s.fromBufferAttribute(smooth, ia);
    if (f.dot(s) < 0) f.negate();
    for (let k = 0; k < 3; k++) {
      const vi = idx.getX(t + k);
      s.fromBufferAttribute(smooth, vi).multiplyScalar(0.4).addScaledVector(f, 0.6).normalize();
      pos[(t + k) * 3] = P.getX(vi); pos[(t + k) * 3 + 1] = P.getY(vi); pos[(t + k) * 3 + 2] = P.getZ(vi);
      nrm[(t + k) * 3] = s.x; nrm[(t + k) * 3 + 1] = s.y; nrm[(t + k) * 3 + 2] = s.z;
    }
  }
  g.dispose();
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
  const ii = new Uint32Array(n);
  for (let i = 0; i < n; i++) ii[i] = i;
  out.setIndex(new THREE.BufferAttribute(ii, 1));
  paintStone(out, seed, moss, y0, y1);
  return out;
}

function paintStone(g: THREE.BufferGeometry, seed: number, moss: boolean, y0: number, y1: number) {
  const pos = g.attributes.position as THREE.BufferAttribute, nrm = g.attributes.normal as THREE.BufferAttribute;
  const col = new Float32Array(pos.count * 3);
  const j = new Jit(seed ^ 0x51);
  const base = new THREE.Color().copy(cStone).lerp(cStoneWarm, j.next() * 0.6).multiplyScalar(j.range(0.9, 1.05));
  const mossAmt = moss ? j.range(0.55, 1.0) : 0.25;
  const mode = _mossMode;
  // meadow pebbles: small dark teal-grey stones, as in the ref
  if (!moss) base.lerp(cStoneDark, 0.4).multiplyScalar(0.85);
  for (let i = 0; i < pos.count; i++) {
    _p.fromBufferAttribute(pos, i);
    _n.fromBufferAttribute(nrm, i);
    const n1 = valueNoise(seed & 0xfff, _p.x * 0.9 + _p.y * 0.7, _p.z * 0.9 - _p.y * 0.5);
    const n2 = valueNoise((seed & 0xfff) + 5, _p.x * 3.1 + _p.y, _p.z * 3.1 - _p.y);
    // shadow sides go cool and dark (painted, not just lit): away from the sun and under-hangs
    const away = Math.max(0, -_n.dot(SUN) + 0.15);
    tmp.copy(base).lerp(cStoneDark, Math.min(0.85, Math.max(0, 0.35 - _n.y) * 0.6 + away * 0.55 + (n2 - 0.5) * 0.3));
    // moss on upward faces, broken by noise; lusher in the shade, sunlit olive on top
    let m = THREE.MathUtils.smoothstep(_n.y + (n1 - 0.5) * 0.7, 0.32, 0.68) * mossAmt;
    if (mode === 1) m *= THREE.MathUtils.smoothstep(_n.y, 0.55, 0.85) * THREE.MathUtils.smoothstep(valueNoise((seed & 0xfff) + 41, _p.x * 1.6, _p.z * 1.6), 0.5, 0.68);
    else if (mode === 2) {
      // soft moss in the joints (low on the flanks) and a soft wash on the crown, faded by a broad
      // noise -- never a hard-edged blotch
      const low = 1 - THREE.MathUtils.smoothstep(_p.y, y0 + 0.05, y0 + Math.max(0.15, (y1 - y0) * 0.4));
      const crown = THREE.MathUtils.smoothstep(_n.y, 0.6, 0.95) * 0.55;
      m = Math.max(low * (1 - THREE.MathUtils.smoothstep(_n.y, 0.3, 0.6)), crown) * (0.35 + 0.65 * n1) * mossAmt;
    }
    if (m > 0) tmp.lerp(tmp.clone().copy(cMossDeep).lerp(cMoss, n1).lerp(cMossSun, Math.max(0, _n.y - 0.75) * 1.5), m);
    // lichen: pale crusty patches (yellow-grey, the odd rust orange) on the weather side
    const li = valueNoise((seed & 0xfff) + 21, _p.x * 1.9 + _p.y * 0.8, _p.z * 1.9 - _p.y * 0.6);
    const lich = THREE.MathUtils.smoothstep(li, 0.55, 0.85) * (moss ? 0.7 : 0.35) * (1 - Math.max(0, _n.y - 0.6) * 2);
    if (lich > 0) tmp.lerp(li > 0.82 ? cLichenRust : cLichen, lich * 0.5);
    // rain stains: darker vertical streaks down the faces
    const streak = valueNoise((seed & 0xfff) + 9, _p.x * 1.7 + _p.z * 1.3, _p.y * 0.18);
    tmp.multiplyScalar(1 - 0.22 * THREE.MathUtils.smoothstep(streak, 0.55, 0.8) * (1 - Math.max(0, _n.y)));
    // damp, darker skirt where it meets the grass
    const foot = 1 - THREE.MathUtils.smoothstep(_p.y, y0 + 0.25, y0 + Math.min(2.2, (y1 - y0) * 0.5));
    tmp.lerp(cMossDeep, foot * 0.4).multiplyScalar(1 - foot * 0.4);
    // noise breakup: mottled weathering at two scales (painted, not plastic)
    const n3 = valueNoise((seed & 0xfff) + 33, _p.x * 4.3 + _p.y * 3.1, _p.z * 4.3 - _p.y * 2.7);
    tmp.multiplyScalar((0.8 + 0.38 * n2) * (0.9 + 0.22 * n3));
    col[i * 3] = tmp.r; col[i * 3 + 1] = tmp.g; col[i * 3 + 2] = tmp.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
}

/** One standing stone on its own (the same stone buildRocks merges), for a toppled one's fall. */
export function pillarGeometry(c: Cyl, quality: number) {
  const out: THREE.BufferGeometry[] = [];
  pillar(c, quality, out);
  return out[0];
}

/**
 * A standing stone: one weathered monolith (or a broad upright slab), irregular in plan,
 * tapering and leaning a little, with chamfered shoulders and a broken, flattened crown for
 * footing. Faceted, moss on the crown and ledges. Stays inside the collision cylinder.
 */
function pillar(c: Cyl, quality: number, out: THREE.BufferGeometry[]) {
  const j = new Jit(hash32(c.variant, 0x9111));
  const h = c.y1 - c.y0;
  const R = c.r * 0.97;
  const sides = quality <= 0 ? 6 : 8;
  const slabby = j.next() < 0.45;
  const aspect = slabby ? j.range(1.7, 2.3) : j.range(1.05, 1.35);
  const rotA = j.range(0, Math.PI * 2), ca = Math.cos(rotA), sa = Math.sin(rotA);
  const rs: number[] = [];
  for (let k = 0; k < sides; k++) rs.push(j.range(0.8, 1.0));
  // rows: (height fraction, radius scale, twist)
  const rows: number[][] = [[-0.06, 1.0]];
  // weathered: uneven rows, the odd frost-crack ledge where the column steps in
  const nr = quality <= 0 ? 4 : 7;
  for (let r = 1; r <= nr; r++) {
    const t = r / (nr + 1);
    const crack = j.next() < 0.3 ? 0.07 : 0;
    rows.push([t * 0.95, (1 - t * j.range(0.3, 0.45)) * j.range(0.93, 1.0)]);
    if (crack) rows.push([t * 0.95 + 0.012, rows[rows.length - 1][1] * (1 - crack)]);
  }
  rows.push([0.97, j.range(0.5, 0.62)]);
  const lx = j.range(-0.12, 0.12), lz = j.range(-0.12, 0.12);
  const ring = (row: number[], ri: number) => {
    const pts: number[][] = [];
    for (let k = 0; k < sides; k++) {
      const a = (k / sides) * Math.PI * 2;
      const wob = 1 + (j.next() - 0.5) * 0.22 * (ri > 0 ? 1 : 0.3);
      let x = Math.cos(a) * rs[k] * row[1] * wob * aspect, z = Math.sin(a) * rs[k] * row[1] * wob;
      const n = Math.hypot(x, z); // keep inside the unit circle after the aspect stretch
      const lim = 1 / Math.max(1, aspect);
      x *= lim; z *= lim;
      void n;
      const t = Math.max(0, row[0]);
      x += lx * t * t; z += lz * t * t;
      const rx = x * ca - z * sa, rz = x * sa + z * ca;
      const d = Math.hypot(rx, rz), f = d > 1 ? 1 / d : 1;
      pts.push([c.x + rx * f * R, c.y0 + row[0] * h + (ri === rows.length - 1 ? j.range(-0.03, 0) * h : 0), c.z + rz * f * R]);
    }
    return pts;
  };
  const rings = rows.map(ring);
  const pos: number[] = [];
  const tri = (p: number[], q: number[], r: number[]) => pos.push(...p, ...q, ...r);
  for (let r = 0; r < rings.length - 1; r++) {
    const A = rings[r], B = rings[r + 1];
    for (let k = 0; k < sides; k++) {
      const k1 = (k + 1) % sides;
      tri(A[k], B[k], A[k1]); tri(A[k1], B[k], B[k1]);
    }
  }
  // crown: a broken, near-flat top at y1
  const T = rings[rings.length - 1];
  let cx = 0, cz = 0;
  for (const p of T) { cx += p[0]; cz += p[2]; }
  const ctr = [cx / sides, c.y1, cz / sides];
  for (let k = 0; k < sides; k++) tri(T[k], ctr, T[(k + 1) % sides]);
  // flat normals, wound outward
  const nrm: number[] = [];
  const A = new THREE.Vector3(), B = new THREE.Vector3(), C = new THREE.Vector3(), N = new THREE.Vector3();
  for (let i = 0; i < pos.length; i += 9) {
    A.fromArray(pos, i); B.fromArray(pos, i + 3); C.fromArray(pos, i + 6);
    N.subVectors(B, A).cross(C.clone().sub(A)).normalize();
    const mx = (A.x + B.x + C.x) / 3 - c.x, mz = (A.z + B.z + C.z) / 3 - c.z, my = (A.y + B.y + C.y) / 3 - (c.y0 + h * 0.5);
    if (N.x * mx + N.z * mz + N.y * my * 0.2 < 0) {
      N.negate();
      for (let k = 0; k < 3; k++) { const t = pos[i + 3 + k]; pos[i + 3 + k] = pos[i + 6 + k]; pos[i + 6 + k] = t; }
    }
    for (let k = 0; k < 3; k++) nrm.push(N.x, N.y, N.z);
  }
  for (let i = 1; i < pos.length; i += 3) if (pos[i] > c.y1) pos[i] = c.y1;
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  const ii = new Uint32Array(pos.length / 3);
  for (let i = 0; i < ii.length; i++) ii[i] = i;
  g.setIndex(new THREE.BufferAttribute(ii, 1));
  paintStone(g, hash32(c.variant, 0x9112), true, c.y0, c.y1);
  out.push(g);
}

/** A pile of 3-4 mossy boulders filling a standing-stone cylinder: the biggest takes the full
 *  height (its crown is the footing at y1), the rest lean against it, all inside the radius. */
function boulderPile(c: Cyl, quality: number, out: THREE.BufferGeometry[]) {
  const j = new Jit(hash32(c.variant, 0x9a1e));
  const R = c.r;
  const a0 = j.range(0, Math.PI * 2);
  out.push(boulder(c.x + Math.cos(a0) * R * 0.15, c.z + Math.sin(a0) * R * 0.15, R * 0.78, c.y0, c.y1, hash32(c.variant, 1), quality, true));
  const n = 2 + (j.next() * 2 | 0);
  for (let k = 0; k < n; k++) {
    const a = a0 + Math.PI * (0.6 + k * 0.7) + j.range(-0.3, 0.3);
    const r = R * j.range(0.3, 0.42);
    const d = R - r - 0.02;
    out.push(boulder(c.x + Math.cos(a) * d, c.z + Math.sin(a) * d, r, c.y0, c.y0 + (c.y1 - c.y0) * j.range(0.3, 0.55), hash32(c.variant, 2 + k), quality, true));
  }
}

/**
 * A broken dry-stone wall filling a ruin box: courses of rounded, uneven field stones of mixed
 * sizes (each a squashed boulder stretched along the wall), the top course broken away toward one
 * end, a few fallen stones at its foot. All inside the box: it's still the wall's collision.
 */
function fieldWall(b: Box, quality: number, out: THREE.BufferGeometry[]) {
  const j = new Jit(hash32(b.variant, 0x2a1, Math.round(b.x * 3)));
  const alongX = b.hx > b.hz;
  const L = (alongX ? b.hx : b.hz) * 2, T = (alongX ? b.hz : b.hx) * 2;
  const h = b.y1 - b.y0;
  // dry-stone: flat slabs stacked in 2-3 courses, each course its own height
  const rows = h > 1.5 ? 3 : 2; // 2-3 courses
  const breakAt = j.range(0.35, 0.8); // the wall stands full height up to here, then crumbles
  const q = quality <= 1 ? -1 : quality;
  const ux = alongX ? 1 : 0, uz = alongX ? 0 : 1;
  _mossMode = 2;
  const rowH: number[] = [];
  for (let r = 0; r < rows; r++) rowH.push(j.range(0.8, 1.2) * (r === 0 ? 1.15 : 1));
  const sumH = rowH.reduce((x, y) => x + y, 0);
  let ya = b.y0;
  for (let r = 0; r < rows; r++) {
    const ch = (h * rowH[r]) / sumH;
    let s = r % 2 ? -j.range(0.3, 0.7) : -j.range(0, 0.3);
    while (s < L) {
      // rounded, noise-displaced field stones of irregular size, flat-bedded (~0.4 as tall as long)
      const sl = j.range(0.55, 1.6) * (r === 0 ? 1.15 : 1);
      const a = Math.max(0, s), e = Math.min(L, s + sl);
      s += sl * j.range(0.88, 1.04);
      if (e - a < 0.3) continue;
      const pos = (a + e) / 2 / L;
      const lost = (rows - r) / rows;
      if (pos > breakAt && j.next() > lost * 1.4 - 0.2) continue;
      if (r > 0 && j.next() < 0.07) continue;
      const len = e - a, th = T * j.range(0.65, 1.0);
      const yb0 = ya - (r === 0 ? 0.22 : 0.08) + j.range(-0.05, 0.05);
      const sh = Math.max(0.22, Math.min(ch * 1.0, 0.4 * len * j.range(0.8, 1.2)));
      const yb = Math.min(b.y1 + 0.08, yb0 + sh);
      const c = -L / 2 + (a + e) / 2;
      const off = j.range(-0.1, 0.1) * T;
      const cx = b.x + ux * c + uz * off, cz = b.z + uz * c + ux * off;
      const g = stretchedStone(cx, cz, len, th, yb0, Math.max(yb0 + 0.25, yb), alongX, hash32(b.variant, r, Math.round(s * 13)), q, true);
      const wk = j.range(0.9, 1.1);
      out.push(tintRGB(tiltAbout(g, cx, (yb0 + yb) / 2, cz, j.range(-0.35, 0.35), j.range(-0.1, 0.1), alongX), 1.22 * wk, 1.0 * wk, 0.74 * wk));
    }
    ya += ch;
  }
  // fallen slabs and chips at the foot, mostly by the broken end, both sides
  const nf = 2 + (j.next() * 3 | 0);
  for (let k = 0; k < nf; k++) {
    const t = (k === 0 ? j.range(0, breakAt) : j.range(breakAt, 1.05)) * L - L / 2, off = j.range(0.6, 1.2) * T * (j.next() < 0.5 ? -1 : 1);
    const sr = j.range(0.2, 0.42);
    const g = boulder(b.x + ux * t + uz * off, b.z + uz * t + ux * off, sr, b.y0 + 0.5, b.y0 + 0.62 + sr * 0.7, hash32(b.variant, 0xfa11, k), q, true);
    out.push(tiltAbout(g, b.x + ux * t + uz * off, b.y0 + 0.6, b.z + uz * t + ux * off, j.range(-1, 1), j.range(-0.35, 0.35), alongX));
  }
  _mossMode = 0;
  for (let k = 0; k < (quality <= 1 ? 3 : 7); k++) {
    const t = j.range(-0.55, 0.55) * L, off = j.range(0.55, 1.1) * T * (j.next() < 0.5 ? -1 : 1);
    chip(b.x + ux * t + uz * off, b.z + uz * t + ux * off, b.y0 + 0.62, j.range(0.08, 0.18), hash32(b.variant, 0xc41, k), out);
  }
}

/** A small chipped stone, half-buried: an 8-face octahedron, squashed and jittered. */
function chip(x: number, z: number, y: number, r: number, seed: number, out: THREE.BufferGeometry[]) {
  let g: THREE.BufferGeometry = new THREE.OctahedronGeometry(1, 0);
  g.deleteAttribute('uv'); g.deleteAttribute('normal');
  g = mergeVertices(g);
  const P = g.attributes.position as THREE.BufferAttribute;
  const j = new Jit(seed);
  const a = j.range(0, Math.PI), ca = Math.cos(a), sa = Math.sin(a), k = j.range(1.1, 1.7);
  for (let i = 0; i < P.count; i++) {
    let px = P.getX(i) * k * j.range(0.8, 1.15), py = P.getY(i) * j.range(0.45, 0.7), pz = P.getZ(i) * j.range(0.8, 1.15);
    if (py < -0.15) py = -0.15;
    const rx = px * ca - pz * sa, rz = px * sa + pz * ca;
    P.setXYZ(i, x + rx * r, y + py * r, z + rz * r);
  }
  const pm = _mossMode; _mossMode = 2;
  out.push(chunky(g, seed, true, y - 0.15 * r, y + 0.7 * r));
  _mossMode = pm;
}

/** Chipped stones scattered round the foot of a rock (sim cylinder at x, z, radius r). */
function debris(x: number, z: number, r: number, world: World, seed: number, n: number, out: THREE.BufferGeometry[]) {
  const j = new Jit(seed);
  const a0 = j.range(0, Math.PI * 2);
  for (let k = 0; k < n; k++) {
    const a = a0 + j.range(-1.4, 1.4), d = r * j.range(1.05, 1.6);
    const px = x + Math.cos(a) * d, pz = z + Math.sin(a) * d;
    const gy = world.terrain(px, pz);
    if (gy === NO_GROUND) continue;
    chip(px, pz, gy, j.range(0.1, 0.24) * Math.min(2, 0.6 + r * 0.3), hash32(seed, k), out);
  }
}

/** Rotate a geometry about (x, y, z): `yaw` about y, then `roll` about the wall axis. */
const _rm = new THREE.Matrix4(), _rm2 = new THREE.Matrix4();
function tiltAbout(g: THREE.BufferGeometry, x: number, y: number, z: number, yaw: number, roll: number, alongX: boolean) {
  _rm.makeTranslation(-x, -y, -z);
  _rm2.makeRotationY(yaw); _rm.premultiply(_rm2);
  if (alongX) _rm2.makeRotationX(roll); else _rm2.makeRotationZ(roll);
  _rm.premultiply(_rm2);
  _rm2.makeTranslation(x, y, z); _rm.premultiply(_rm2);
  g.applyMatrix4(_rm);
  return g;
}

/** A field stone: a rounded boulder of footprint `th` stretched to `len` along the wall axis. */
function stretchedStone(x: number, z: number, len: number, th: number, y0: number, y1: number, alongX: boolean, seed: number, quality: number, moss: boolean) {
  const g = boulder(0, 0, th / 2, y0, y1, seed, quality, moss);
  const k = Math.max(1, len / th);
  const sx = alongX ? k : 1, sz = alongX ? 1 : k;
  const P = g.attributes.position as THREE.BufferAttribute, N = g.attributes.normal as THREE.BufferAttribute;
  for (let i = 0; i < P.count; i++) {
    P.setXYZ(i, x + P.getX(i) * sx, P.getY(i), z + P.getZ(i) * sz);
    _p.set(N.getX(i) / sx, N.getY(i), N.getZ(i) / sz).normalize();
    N.setXYZ(i, _p.x, _p.y, _p.z);
  }
  return g;
}

function crate(b: Box, g: Geo) {
  const kind = b.variant % 3;
  const h = b.y1 - b.y0;
  const cy = (b.y0 + b.y1) / 2;
  if (kind === 2) {
    // an appliance box: cardboard with a red band and an arrow
    g.box(b.x, cy, b.z, b.hx, h / 2, b.hz, cCard);
    g.box(b.x, b.y0 + h * 0.62, b.z, b.hx + 0.01, 0.12, b.hz + 0.01, cRed, 0, { top: false });
    g.box(b.x, b.y1 - 0.03, b.z, b.hx * 0.1, 0.03, b.hz + 0.005, C(PAL.tan, 0.8), 0, { top: true });
    g.box(b.x - b.hx * 0.3, b.y0 + h * 0.35, b.z, 0.04, h * 0.14, b.hz + 0.012, cInk, 0, { top: false });
    return;
  }
  const base = (kind === 0 ? cWood : C(PAL.olive)).clone();
  g.box(b.x, cy, b.z, b.hx - 0.02, h / 2, b.hz - 0.02, base);
  const fr = cWoodDark;
  const t = 0.07;
  for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) g.box(b.x + sx * (b.hx - t), cy, b.z + sz * (b.hz - t), t, h / 2, t, fr, 0, { top: true });
  for (const yy of [b.y0 + t, b.y1 - t]) {
    g.box(b.x, yy, b.z - b.hz + t * 0.5, b.hx, t, t * 0.6, fr, 0, { top: yy > cy });
    g.box(b.x, yy, b.z + b.hz - t * 0.5, b.hx, t, t * 0.6, fr, 0, { top: yy > cy });
    g.box(b.x - b.hx + t * 0.5, yy, b.z, t * 0.6, t, b.hz, fr, 0, { top: yy > cy });
    g.box(b.x + b.hx - t * 0.5, yy, b.z, t * 0.6, t, b.hz, fr, 0, { top: yy > cy });
  }
  g.box(b.x, cy, b.z - b.hz - 0.005, b.hx * 0.35, h * 0.1, 0.01, kind === 0 ? cInk : C(PAL.paperCream), 0, { top: false });
}
